import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Button } from '@/shared/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Loader2 } from 'lucide-react';
import { useUpdateServiceLineType } from '@/domains/service-lines/services/account-service-lines';
import { ServiceLine, ServiceLineType } from '@/shared/types/serviceLineTypes';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';

interface ServiceLineTypeDialogProps {
  open: boolean;
  serviceLine: ServiceLine;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const ServiceLineTypeDialog = ({
  open,
  onOpenChange,
  onSuccess,
  serviceLine,
}: ServiceLineTypeDialogProps) => {
  const [selectedType, setSelectedType] = useState<ServiceLineType | null>(
    serviceLine?.type ?? null
  );
  const updateServiceLineType = useUpdateServiceLineType();

  useEffect(() => {
    if (!open) {
      setSelectedType(serviceLine?.type ?? null);
    }
  }, [open, serviceLine?.type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedType) {
      return;
    }

    try {
      await updateServiceLineType.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
        type: selectedType,
      });
      showSuccess(SUCCESS.SERVICE_LINE.UPDATED_TYPE);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.UPDATE_TYPE);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Service Line Type</DialogTitle>
          <DialogDescription>
            Select the type for this service line. Primary service lines are used
            for main connectivity, while Backup lines are reserved for failover.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="service-line-type">Service Line Type</Label>
            <Select
              value={selectedType ?? undefined}
              onValueChange={(value) =>
                setSelectedType(value as ServiceLineType)
              }
            >
              <SelectTrigger id="service-line-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(ServiceLineType).map((option) => {
                  return (
                    <SelectItem
                      key={option}
                      value={option}
                    >
                      {option}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateServiceLineType.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateServiceLineType.isPending || !selectedType}>
              {updateServiceLineType.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
